/**
 * @file markdownCell组件
 * @author FranckChen(chenfan02@baidu.com)
 */

import React, {memo, useMemo} from 'react';
import {crlf} from 'eol';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js/lib/core';
import createDomPurify, {DOMPurifyI} from 'dompurify';
import mk from '@abreto/markdown-it-katex';

import {IMarkdownCell} from '../../types/markdownCell';

const linkEnhancePlugin = function (md: MarkdownIt) {
    md.renderer.rules.link_open = function (tokens, index, _opts, _env, renderer) {
        const currentToken = tokens[index];

        currentToken.attrs = currentToken.attrs || [];
        currentToken.attrs.push(['target', '_blank']);

        return renderer.renderToken(tokens, index, {});
    };
};

const MarkdownCell: React.FC<IMarkdownCell> = memo(function (props) {
    const {source} = props;
    // 拼接markdown源代码
    const rawMarkdownSource = useMemo<string>(() => {
        return crlf(source.join(''));
    }, [source]);

    const purifier: DOMPurifyI = useMemo<DOMPurifyI>(() => {
        return createDomPurify(window);
    }, []);

    const mdCompiler = useMemo<MarkdownIt>(() => {
        const md = new MarkdownIt({
            html: true,
            typographer: true,
            // 文档代码高亮
            highlight(str, lang) {
                const availableLang = hljs.getLanguage(lang);

                if (availableLang) {
                    try {
                        // 根据在markdown中指定的语言类型高亮代码
                        return [
                            '<pre><code class="hljs">',
                            hljs.highlight(lang, str).value,
                            '</pre></code>'
                        ].join('');
                    }
                    catch {
                        // 自动检测语言类型并高亮
                        return [
                            '<pre><code class="hljs">',
                            hljs.highlightAuto(str).value,
                            '</pre></code>'
                        ].join('');
                    }
                }

                // 自动检测语言类型并高亮
                return [
                    '<pre><code class="hljs">',
                    hljs.highlightAuto(str).value,
                    '</pre></code>'
                ].join('');
            }
        });

        md.use(mk).use(linkEnhancePlugin);
        return md;
    }, []);

    const createDangerouslyHTMLMarkup = (html: string) => {
        return {
            __html: html
        };
    };

    const html = useMemo<string>(() => {
        const dirty = mdCompiler.render(rawMarkdownSource);
        return purifier.sanitize(dirty, {
            ADD_ATTR: ['target']
        });
    }, [purifier, mdCompiler, rawMarkdownSource]);

    return (
        <div className="ai-np-markdown-cell-wrapper">
            <div dangerouslySetInnerHTML={createDangerouslyHTMLMarkup(html)}
                 className="ai-np-markdown-cell-content" />
        </div>
    );
});

export default MarkdownCell;
